/** Internal maintenance cost reconciliation and independently approved chargebacks. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { useTeams } from "@/features/admin/identity/queries";
import { useCompleteCostCenters } from "@/features/admin/reference/queries";
import {
  type Chargeback,
  type CostFilters,
  type CostRow,
  exportInternalCosts,
  useApproveChargeback,
  useChargeback,
  useChargebacks,
  useCreateChargeback,
  useInternalCosts,
  useVoidChargeback,
} from "@/features/billing/queries";
import { DataTable } from "@/shared/components/data-table";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { TableToolbar, type TableFilterDefinition } from "@/shared/components/table-toolbar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { AssetSelect, PartyPicker, SitePicker } from "@/shared/components/pickers";
import { formatDateTime, formatMoney, formatQuantity } from "@/shared/lib/format";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Field, Textarea } from "@/shared/ui/form";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

/** URL contract for the internal finance route, including date/allocation facets. */
export const internalFinanceSearchSchema = z.object({
  site_id: z.string().optional(),
  vessel_id: z.string().optional(),
  asset_id: z.string().optional(),
  team_id: z.string().optional(),
  operating_unit_id: z.string().optional(),
  cost_center_id: z.string().optional(),
  fault_id: z.string().optional(),
  since: z.string().optional(),
  until: z.string().optional(),
  scope: z.enum(["owned", "external", "all"]).optional(),
  tab: z.string().optional(),
  chargeback_id: z.string().optional(),
  chargeback_status: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  size: z.coerce.number().int().min(10).max(200).optional().catch(undefined),
});

type FinanceSearch = z.infer<typeof internalFinanceSearchSchema>;

function FinanceFilterPicker({
  label,
  value,
  trigger,
  render,
}: {
  label: string;
  value?: string;
  trigger?: ReactElement;
  render: () => ReactElement;
}): ReactElement {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="w-full justify-start" aria-label={label}>
            <span className="min-w-0 truncate">{value ?? label}</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3 p-3" aria-label={label}>
        {render()}
      </PopoverContent>
    </Popover>
  );
}

/** A responsive cost table reused for the live report and immutable accounting evidence. */
export function CostTable({
  rows,
  onCreate,
  controls = false,
}: {
  rows: CostRow[];
  onCreate?: (row: CostRow) => void;
  controls?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const money = (amount: string | null, currency: string | null): string =>
    amount !== null && currency ? formatMoney({ amount, currency }) : "—";
  const columns: ColumnDef<CostRow>[] = [
    { id: "code", accessorKey: "code", header: t("finance.workOrder") },
    {
      id: "equipment",
      header: t("finance.equipment"),
      cell: ({ row }) => (
        <div>
          <AssetReference
            id={row.original.asset.id}
            name={row.original.asset.name}
            context="history"
          />
          <div className="text-xs text-muted-foreground">
            {row.original.vessel ? (
              <AssetReference
                id={row.original.vessel.id}
                name={row.original.vessel.name}
                context="history"
              />
            ) : (
              "—"
            )}
          </div>
        </div>
      ),
    },
    {
      id: "allocation",
      header: t("finance.allocation"),
      cell: ({ row }) => (
        <div>
          {row.original.operating_unit?.name ?? t("finance.unassigned")}
          <div className="text-xs text-muted-foreground">
            {row.original.cost_center?.code ?? t("finance.unassigned")}
          </div>
        </div>
      ),
    },
    {
      id: "team",
      header: t("finance.team"),
      cell: ({ row }) => row.original.team?.name ?? t("finance.unassigned"),
    },
    {
      id: "date",
      header: t("finance.date"),
      cell: ({ row }) => formatDateTime(row.original.cost_at),
    },
    {
      accessorKey: "hours",
      header: t("finance.hours"),
      cell: ({ row }) => formatQuantity(row.original.hours),
    },
    {
      accessorKey: "labour_cost",
      header: t("finance.labour"),
      cell: ({ row }) => money(row.original.labour_cost, row.original.currency),
    },
    {
      accessorKey: "parts_cost",
      header: t("finance.parts"),
      cell: ({ row }) => money(row.original.parts_cost, row.original.currency),
    },
    {
      accessorKey: "vendor_cost",
      header: t("finance.vendor"),
      cell: ({ row }) => money(row.original.vendor_cost, row.original.currency),
    },
    {
      id: "total",
      header: t("finance.knownTotal"),
      cell: ({ row }) => money(row.original.total_cost, row.original.currency),
    },
    {
      id: "costing",
      header: t("finance.costing"),
      cell: ({ row }) => (
        <div>
          <Badge tone={row.original.cost_status === "costed" ? "success" : "warning"}>
            {t(`finance.costStatus.${row.original.cost_status}`)}
          </Badge>
          <div className="text-xs">
            {t(row.original.accepted ? "finance.accepted" : "finance.notAccepted")}
          </div>
        </div>
      ),
    },
    ...(onCreate
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: ({ row }: { row: { original: CostRow } }): ReactElement => (
              <Button
                size="sm"
                variant="outline"
                disabled={!row.original.accepted}
                onClick={() => onCreate(row.original)}
              >
                {t("billing.create")}
              </Button>
            ),
          },
        ]
      : []),
  ];
  return controls ? (
    <CompleteDataTable
      columns={columns}
      data={rows}
      minWidth={1200}
      sortFields={[
        { value: "code", label: t("finance.workOrder"), valueOf: (row) => row.code },
        { value: "equipment", label: t("finance.equipment"), valueOf: (row) => row.asset.name },
        { value: "date", label: t("finance.date"), valueOf: (row) => row.cost_at },
      ]}
    />
  ) : (
    <DataTable columns={columns} data={rows} minWidth={1200} />
  );
}

/** Explicit financial-exception dialog before freezing accepted costs. */
export function CreateChargebackDialog({
  row,
  onClose,
}: {
  row: CostRow;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const create = useCreateChargeback();
  const form = useForm({ initial: { exception_reason: "" }, idPrefix: "chargeback" });
  const submit = async (): Promise<void> => {
    if (
      await form.submit((values) =>
        create.mutateAsync({
          work_order_id: row.work_order_id,
          exception_reason: values.exception_reason,
        }),
      )
    )
      onClose();
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("billing.create")}
      description={t("billing.snapshotWarning", { code: row.code })}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field
        id="chargeback-exception_reason"
        label={t("billing.exceptionReason")}
        hint={t("billing.exceptionHint")}
      >
        <Textarea {...form.field("exception_reason")} />
      </Field>
    </FormDialog>
  );
}

/** Review all immutable snapshot lines and record independent approval. */
export function ChargebackDetailDialog({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can, me } = useAuth();
  const detail = useChargeback(id);
  const approve = useApproveChargeback();
  const voidDraft = useVoidChargeback();
  const form = useForm({
    initial: { reason: "" },
    idPrefix: "approval",
    schema: z.object({ reason: z.string().trim().min(1, t("app.required")) }),
  });
  const chargeback = detail.data;
  const allowed =
    chargeback?.status === "draft" &&
    can("billing.chargeback.approve") &&
    chargeback.created_by_id !== me?.membership_id;
  const canVoid = chargeback?.status === "draft" && can("billing.chargeback.create");
  const submit = async (): Promise<void> => {
    await form.submit((values) => approve.mutateAsync({ id, reason: values.reason }));
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("billing.detail")}
      onSubmit={
        allowed
          ? submit
          : () => {
              onClose();
              return Promise.resolve();
            }
      }
      submitLabel={t(allowed ? "billing.approve" : "app.close")}
      submitting={form.submitting}
      error={form.errors._}
      dirty={form.dirty}
      wide
    >
      {detail.isPending ? (
        <LoadingState />
      ) : detail.error ? (
        <ErrorState error={detail.error} />
      ) : chargeback ? (
        <>
          <p className="break-words text-sm">{chargeback.chain_key}</p>
          <StatusBadge value={chargeback.status} namespace="billing.status" />
          <p className="font-semibold">
            {formatMoney({ amount: chargeback.total, currency: chargeback.currency })}
          </p>
          <p className="text-sm text-muted-foreground">{t("billing.internalOnly")}</p>
          <p>
            {t("billing.createdBy")}: {chargeback.created_by_name}
          </p>
          {chargeback.exception_reason ? (
            <p role="note">
              {t("billing.exceptionReason")}: {chargeback.exception_reason}
            </p>
          ) : null}
          <CostTable rows={chargeback.snapshot.work_orders} />
          <div className="space-y-2 text-sm">
            {chargeback.snapshot.work_orders.flatMap((row) =>
              row.labour.map((entry) => (
                <p key={entry.id}>
                  {row.code}: {entry.hours} {t("finance.hours")} ×{" "}
                  {entry.rate ?? t("finance.costStatus.uncosted")}{" "}
                  {entry.currency || t("finance.unknownCurrency")} = {entry.cost}
                </p>
              )),
            )}
          </div>
          <div className="space-y-2 text-sm">
            {chargeback.snapshot.work_orders.flatMap((row) =>
              row.parts.map((part) => (
                <p key={part.id}>
                  {row.code}: {part.quantity} × {part.unit_cost ?? t("finance.costStatus.uncosted")}{" "}
                  {part.currency === "" ? t("finance.unknownCurrency") : part.currency} ={" "}
                  {part.cost}
                </p>
              )),
            )}
          </div>
          {chargeback.approved_at ? (
            <p>
              {t("billing.approvedAt")}: {formatDateTime(chargeback.approved_at)} —{" "}
              {chargeback.approved_by_name} — {chargeback.approval_reason}
            </p>
          ) : null}
          {chargeback.voided_at ? (
            <p>
              {t("billing.voidReason")}: {chargeback.void_reason}
            </p>
          ) : null}
          {canVoid ? (
            <Button
              type="button"
              variant="outline"
              disabled={voidDraft.isPending}
              onClick={() => {
                void form.submit((values) => voidDraft.mutateAsync({ id, reason: values.reason }));
              }}
            >
              {t("billing.void")}
            </Button>
          ) : null}
          {allowed || canVoid ? (
            <Field
              id="approval-reason"
              label={t("billing.approvalReason")}
              error={form.errors.reason}
            >
              <Textarea {...form.field("reason")} required />
            </Field>
          ) : chargeback.status === "draft" ? (
            <p className="text-sm">{t("billing.independentApproval")}</p>
          ) : null}
        </>
      ) : null}
    </FormDialog>
  );
}

/** Filterable finance report with fault-chain reconciliation and internal approvals. */
export function InternalFinancePage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const search: FinanceSearch = useSearch({ strict: false });
  const update = (patch: Partial<FinanceSearch>): void => {
    void navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      resetScroll: false,
    });
  };
  const { tab: requestedTab, chargeback_id: detailId } = search;
  const filters: CostFilters = {
    site_id: search.site_id,
    vessel_id: search.vessel_id,
    asset_id: search.asset_id,
    team_id: search.team_id,
    operating_unit_id: search.operating_unit_id,
    cost_center_id: search.cost_center_id,
    fault_id: search.fault_id,
    since: search.since,
    until: search.until,
    scope: search.scope,
  };
  const tab =
    requestedTab === "chargebacks" && can("billing.chargeback.view")
      ? "chargebacks"
      : can("reporting.report.cost")
        ? "costs"
        : "chargebacks";
  const report = useInternalCosts(filters, can("reporting.report.cost"));
  const chargebacks = useChargebacks(can("billing.chargeback.view"), search.chargeback_status);
  const teams = useTeams();
  const centers = useCompleteCostCenters(search.operating_unit_id);
  const [creating, setCreating] = useState<CostRow | null>(null);
  const setDetailId = (id: string | undefined): void => update({ chargeback_id: id });
  const [exportError, setExportError] = useState<unknown>();
  const picker = (
    key: "site_id" | "vessel_id" | "asset_id" | "operating_unit_id",
    label: string,
    render: () => ReactElement,
  ): TableFilterDefinition => ({
    id: key,
    label,
    kind: "custom",
    value: search[key],
    onChange: () => undefined,
    onRemove: () =>
      update({
        [key]: undefined,
        ...(key === "operating_unit_id" ? { cost_center_id: undefined } : {}),
      }),
    renderEditor: (trigger) => (
      <FinanceFilterPicker label={label} value={search[key]} trigger={trigger} render={render} />
    ),
  });
  const definitions: TableFilterDefinition[] = [
    {
      id: "scope",
      label: t("finance.scope"),
      kind: "select",
      prominent: true,
      value: (search.scope ?? "owned") === "all" ? undefined : (search.scope ?? "owned"),
      options: ["owned", "external"].map((value) => ({
        value,
        label: t(`finance.scopes.${value}`),
      })),
      onChange: (value) =>
        update({ scope: value === "owned" || value === "external" ? value : "all" }),
      onRemove: () => update({ scope: "all" }),
    },
    picker("site_id", t("finance.site"), () => (
      <SitePicker
        id="finance-site"
        aria-label={t("finance.site")}
        value={search.site_id ?? ""}
        onChange={(id) => update({ site_id: id ?? undefined })}
      />
    )),
    picker("vessel_id", t("finance.vessel"), () => (
      <AssetSelect
        id="finance-vessel"
        aria-label={t("finance.vessel")}
        vesselsOnly
        value={search.vessel_id ?? ""}
        onChange={(id) => update({ vessel_id: id ?? undefined })}
      />
    )),
    picker("asset_id", t("finance.equipment"), () => (
      <AssetSelect
        id="finance-equipment"
        aria-label={t("finance.equipment")}
        value={search.asset_id ?? ""}
        onChange={(id) => update({ asset_id: id ?? undefined })}
      />
    )),
    picker("operating_unit_id", t("parties.operatingUnit"), () => (
      <PartyPicker
        id="finance-unit"
        aria-label={t("parties.operatingUnit")}
        role="operator"
        value={search.operating_unit_id ?? ""}
        onChange={(id) => update({ operating_unit_id: id ?? undefined, cost_center_id: undefined })}
      />
    )),
    {
      id: "cost_center_id",
      label: t("parties.costCenters.singular"),
      kind: "select",
      value: search.cost_center_id,
      options: (centers.data ?? []).map((center) => ({
        value: center.id,
        label: `${center.code} - ${center.name}`,
      })),
      onChange: (value) =>
        update({ cost_center_id: typeof value === "string" ? value : undefined }),
      onRemove: () => update({ cost_center_id: undefined }),
    },
    {
      id: "team_id",
      label: t("finance.team"),
      kind: "select",
      value: search.team_id,
      options: (teams.data ?? []).map((team) => ({ value: team.id, label: team.name })),
      onChange: (value) => update({ team_id: typeof value === "string" ? value : undefined }),
      onRemove: () => update({ team_id: undefined }),
    },
    ...(["since", "until"] as const).map((key): TableFilterDefinition => ({
      id: key,
      label: t(key === "since" ? "finance.since" : "finance.until"),
      kind: "date",
      boundary: key === "since" ? "start" : "end",
      value: search[key],
      onChange: (value) => update({ [key]: typeof value === "string" ? value : undefined }),
      onRemove: () => update({ [key]: undefined }),
    })),
  ];
  const chargebackColumns: ColumnDef<Chargeback>[] = [
    {
      accessorKey: "chain_key",
      header: t("billing.chain"),
      cell: ({ row }) =>
        row.original.snapshot.work_orders.map((workOrder) => workOrder.code).join(" + "),
    },
    {
      id: "status",
      header: t("app.status"),
      cell: ({ row }) => <StatusBadge value={row.original.status} namespace="billing.status" />,
    },
    {
      id: "total",
      header: t("finance.knownTotal"),
      cell: ({ row }) =>
        formatMoney({ amount: row.original.total, currency: row.original.currency }),
    },
    {
      id: "date",
      header: t("billing.createdAt"),
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      id: "actions",
      header: t("app.actions"),
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => setDetailId(row.original.id)}>
          {t("billing.review")}
        </Button>
      ),
    },
  ];
  return (
    <>
      <PageHeader title={t("finance.title")} subtitle={t("billing.internalOnly")} />
      <Tabs
        value={tab}
        onValueChange={(value) => update({ tab: value, page: undefined, sort: undefined })}
      >
        <TabsList className="mb-4 flex w-full flex-wrap justify-start">
          {can("reporting.report.cost") ? (
            <TabsTrigger value="costs">{t("finance.title")}</TabsTrigger>
          ) : null}
          {can("billing.chargeback.view") ? (
            <TabsTrigger value="chargebacks">{t("billing.title")}</TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="costs">
          <TableToolbar
            filters={definitions}
            onReset={() =>
              update({
                site_id: undefined,
                vessel_id: undefined,
                asset_id: undefined,
                operating_unit_id: undefined,
                cost_center_id: undefined,
                team_id: undefined,
                since: undefined,
                until: undefined,
                scope: undefined,
                page: undefined,
              })
            }
          />
          {can("reporting.report.export") ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setExportError(undefined);
                  void exportInternalCosts(filters).catch(setExportError);
                }}
              >
                {t("finance.export")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExportError(undefined);
                  void exportInternalCosts(filters, "xlsx").catch(setExportError);
                }}
              >
                {t("finance.exportXlsx")}
              </Button>
            </div>
          ) : null}
          {exportError ? <p role="alert">{errorMessage(exportError)}</p> : null}
          {report.isPending ? (
            <LoadingState />
          ) : report.error ? (
            <ErrorState error={report.error} />
          ) : report.data ? (
            <>
              <div className="my-4 flex flex-wrap gap-4">
                {report.data.totals.map((total) => (
                  <p key={total.currency} className="rounded-lg border p-3">
                    <span className="block text-sm">{t("finance.knownTotal")}</span>
                    <strong>
                      {formatMoney({ amount: total.total_cost, currency: total.currency })}
                    </strong>
                  </p>
                ))}
              </div>
              {report.data.uncosted_entries || report.data.mixed_currency_rows ? (
                <p role="alert" data-testid="finance-cost-warning" className="mb-3 text-warning">
                  {t("finance.exceptions", {
                    count: report.data.uncosted_entries,
                    mixed: report.data.mixed_currency_rows,
                  })}
                </p>
              ) : null}
              <CostTable
                rows={report.data.rows}
                controls
                onCreate={can("billing.chargeback.create") ? setCreating : undefined}
              />
              <h2 className="my-4 text-lg font-semibold">{t("finance.chains")}</h2>
              <p className="mb-3 text-sm text-muted-foreground">{t("finance.chainScopeHint")}</p>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {report.data.chains.map((chain) => (
                  <section className="min-w-0 rounded-lg border p-3" key={chain.chain_key}>
                    <p className="break-words text-sm">{chain.work_order_codes.join(" + ")}</p>
                    {chain.totals.map((total) => (
                      <p key={total.currency}>
                        {formatMoney({
                          amount: total.total_cost,
                          currency: total.currency,
                        })}
                      </p>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      {t(chain.all_accepted ? "finance.accepted" : "finance.notAccepted")}
                    </p>
                    {chain.uncosted_entries ? (
                      <p>{t("finance.chainExceptions", { count: chain.uncosted_entries })}</p>
                    ) : null}
                    {!chain.complete_scope ? <p role="alert">{t("finance.partialScope")}</p> : null}
                  </section>
                ))}
              </div>
            </>
          ) : null}
        </TabsContent>
        <TabsContent value="chargebacks">
          <TableToolbar
            filters={[
              {
                id: "chargeback_status",
                label: t("app.status"),
                kind: "select",
                prominent: true,
                value: search.chargeback_status,
                options: ["draft", "approved", "void"].map((value) => ({
                  value,
                  label: t(`billing.status.${value}`),
                })),
                onChange: (value) =>
                  update({ chargeback_status: typeof value === "string" ? value : undefined }),
                onRemove: () => update({ chargeback_status: undefined }),
              },
            ]}
            onReset={() => update({ chargeback_status: undefined })}
          />
          {chargebacks.query.isPending ? (
            <LoadingState />
          ) : chargebacks.query.error ? (
            <ErrorState error={chargebacks.query.error} />
          ) : (
            <DataTable
              columns={chargebackColumns}
              data={chargebacks.items}
              hasMore={chargebacks.hasMore}
              onLoadMore={chargebacks.loadMore}
              isLoadingMore={chargebacks.isLoadingMore}
            />
          )}
        </TabsContent>
      </Tabs>
      {creating ? (
        <CreateChargebackDialog row={creating} onClose={() => setCreating(null)} />
      ) : null}
      {detailId ? (
        <ChargebackDetailDialog id={detailId} onClose={() => setDetailId(undefined)} />
      ) : null}
    </>
  );
}
